/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react';
import { Row, Col, Progress, Modal, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useTranslator, useAppContext } from '@front-activerse/common-components';

const DayPlanStats = ({ meals }) => {
  const { t } = useTranslator();
  const [showModal, setShowModal] = useState(false);

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const {
    images: { doneKcal },
  } = useAppContext();

  let sumKcal = 0;
  let eatenKcal = 0;

  let sumProteins = 0;
  let eatenProteins = 0;

  let sumFats = 0;
  let eatenFats = 0;

  let sumCarbs = 0;
  let eatenCarbs = 0;

  meals.forEach((meal) => {
    sumKcal += meal.kcal;
    sumProteins += meal.macro.protein.grams;
    sumFats += meal.macro.fat.grams;
    sumCarbs += meal.macro.carbohydrates.grams;

    if (meal.isEaten) {
      eatenKcal += meal.kcal;
      eatenProteins += meal.macro.protein.grams;
      eatenFats += meal.macro.fat.grams;
      eatenCarbs += meal.macro.carbohydrates.grams;
    }
  });

  const round = (x) => {
    return Math.round(x * 10) / 10;
  };

  const percentageDonutKcal = (eatenKcal / sumKcal) * 107; // 107 = 2 * PI * R
  const percentageProteins = (eatenProteins / sumProteins) * 100;
  const percentageFats = (eatenFats / sumFats) * 100;
  const percentageCarbs = (eatenCarbs / sumCarbs) * 100;

  return (
    <>
      <Row>
        <Col xl={{ size: 8, offset: 2 }}>
          <div className="meal-plan-stats">
            <div className="d-inline-flex align-items-center">
              <div className="donut-chart me-2 ">
                <svg className="svg-donut" width="40px" height="40px">
                  <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                    <circle
                      className="donut"
                      strokeDasharray={`${percentageDonutKcal}, 900`}
                      strokeWidth="5"
                      strokeLinecap="round"
                      transform="translate(20.000000, 20.000000) scale(1, -1) translate(-20.000000, -20.000000) "
                      cx="20"
                      cy="20"
                      r="17"
                      style={{
                        '--endvalue': percentageDonutKcal,
                      }}
                    ></circle>
                  </g>
                </svg>
                {percentageDonutKcal >= 107 && <img src={doneKcal} alt="done" className="done" />}
              </div>
              <div>
                <p className="mb-0 description medium text-text-main-color">
                  {t('mealPlan.nutritionalInfo.calories.title')}
                </p>
                <p className="mb-0 small text-text-variant-color">
                  <span className="medium text-text-main-color">{round(eatenKcal)} kcal</span> /{' '}
                  {round(sumKcal)} kcal
                </p>
              </div>
            </div>
            <hr className="my-m" />
            <Row className="mb-m">
              <Col>
                <p className="description text-text-main-color mb-xxs">
                  {t('mealPlan.nutritionalInfo.proteins.title')}
                </p>
                <Progress className="progress-1 mb-xxs" value={percentageProteins} />
                <p className="mb-0 small text-text-variant-color">
                  <span className="medium text-text-main-color">{round(eatenProteins)} g</span> /{' '}
                  {round(sumProteins)} g
                </p>
              </Col>
              <Col>
                <p className="description text-text-main-color mb-xxs">
                  {t('mealPlan.nutritionalInfo.fats.title')}
                </p>
                <Progress className="progress-2 mb-xxs" value={percentageFats} />
                <p className="mb-0 small text-text-variant-color">
                  <span className="medium text-text-main-color">{round(eatenFats)} g</span> /{' '}
                  {round(sumFats)} g
                </p>
              </Col>
              <Col>
                <p className="description text-text-main-color mb-xxs">
                  {t('mealPlan.nutritionalInfo.carbs.title')}
                </p>
                <Progress className="progress-3 mb-xxs" value={percentageCarbs} />
                <p className="mb-0 small text-text-variant-color">
                  <span className="medium text-text-main-color">{round(eatenCarbs)} g</span> /{' '}
                  {round(sumCarbs)} g
                </p>
              </Col>
            </Row>
            <p className="description text-text-variant-color pt-s mb-0">
              {t('mealPlan.nutritionalInfo.infoContent')}
            </p>

            <p className="description text-text-variant-color pt-s mb-0">
              {t('mealPlan.sourceInfo.content')}{' '}
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  toggleModal();
                }}
              >
                {t('mealPlan.sourceInfo.button')}
              </a>
            </p>
          </div>
        </Col>
      </Row>

      <Modal isOpen={showModal} toggle={toggleModal} centered size="md">
        <ModalBody>
          <h4 className="title-medium semi-bold">{t('mealPlan.sourceInfoPopup.title')}</h4>
          <p className="text-text-variant-color multi-line">
            {t('mealPlan.sourceInfoPopup.content.part1')}
          </p>
          <p className="text-text-variant-color multi-line">
            {t('mealPlan.sourceInfoPopup.content.part2')}
          </p>
          <ul className="text-text-variant-color multi-line ps-3">
            <li className="mb-2">
              Harris JA, Benedict FG. A Biometric Study of Human Basal Metabolism. Proc Natl Acad
              Sci U S A. 1918;4(12):370–3. <br />
              <a className="fw-normal" href="https://pmc.ncbi.nlm.nih.gov/articles/PMC1091492/">
                https://pmc.ncbi.nlm.nih.gov/articles/PMC1091492/
              </a>
            </li>
            <li className="mb-2">
              Frankenfield D, Roth-Yousey L, Compher C. Comparison of predictive equations for
              resting metabolic rate in healthy nonobese and obese adults: a systematic review. J Am
              Diet Assoc. 2005;105(5):775–89. <br />
              <a className="fw-normal" href="https://pubmed.ncbi.nlm.nih.gov/15883556/">
                https://pubmed.ncbi.nlm.nih.gov/15883556/
              </a>
            </li>
            <li className="mb-2">
              FAO/WHO/UNU. Human energy requirements: Report of a Joint Expert Consultation. FAO
              Food and Nutrition Technical Report Series 1. 2004. <br />
              <a className="fw-normal" href="https://www.fao.org/4/y5686e/y5686e00.htm">
                https://www.fao.org/4/y5686e/y5686e00.htm
              </a>
            </li>
            <li className="mb-2">
              Halton T. L. Hu F. B. The effects of high protein diets on thermogenesis, satiety and
              weight loss: a critical review. J Am Coll Nutr 2004 23(5): 373-85. <br />
              <a className="fw-normal" href="https://pubmed.ncbi.nlm.nih.gov/15466943/">
                https://pubmed.ncbi.nlm.nih.gov/15466943/
              </a>
            </li>
            <li className="mb-2">
              Westerterp - Plantenga M. S. et al. Dietary protein, weight loss, and weight
              maintenance. Ann. Rew. Nutr. 2009 29:21-41 <br />
              <a className="fw-normal" href="https://pubmed.ncbi.nlm.nih.gov/19400750/">
                https://pubmed.ncbi.nlm.nih.gov/19400750/
              </a>
            </li>
            <li className="mb-2">
              Keller U. Dietary proteins in obesity and in diabetes. Int J Vitam Nutr Res 2011
              81:125-33 <br />
              <a className="fw-normal" href="https://pubmed.ncbi.nlm.nih.gov/22139563/">
                https://pubmed.ncbi.nlm.nih.gov/22139563/
              </a>
            </li>
            <li>
              Leidy H. J. Clifton P. M et al. The role of protein in weight loss and maintenance. Am
              J Clin Nutr 2015 101(6):1320S-1329S <br />
              <a className="fw-normal" href="https://pubmed.ncbi.nlm.nih.gov/25926512/">
                https://pubmed.ncbi.nlm.nih.gov/25926512/
              </a>
            </li>
          </ul>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" className="w-100" onClick={toggleModal}>
            {t('app.mealPlan.mealsCost.popupButton')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default DayPlanStats;
